import React from 'react'
import { Badge, Tile, TileCaption, TileOverlay } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { BREAKPOINTS } from '@mc/media'
import { paths } from '@mc/paths'
import Image from 'components/LegacyImage'
import Link from 'components/Link/CustomLink'
import { ContentType, CourseMeta, OutcomeContent } from 'types/data'
import styles from './OutcomeTile.module.scss'

const getTileLink = (content: OutcomeContent, courseMeta: CourseMeta) => {
  if (content.contentType === ContentType.CHAPTER) {
    return paths.chaptersWatch(courseMeta.courseSlug, content.slug)
  }

  if (content.contentType === ContentType.MODULE) {
    return paths.sessionsClassHome(content.courseSlug)
  }

  if (content.contentType === ContentType.SUBCHAPTER) {
    return paths.chaptersWatch(courseMeta.courseSlug, content.chapterSlug)
  }

  return ''
}

export const LessonContentTile = ({
  content,
  courseMeta,
  isPrioritized,
  trackOnClick,
}: {
  content: OutcomeContent
  courseMeta: CourseMeta
  isPrioritized: boolean
  trackOnClick: () => void
}) => {
  const { t } = useTranslation('@mc/next')

  const getDurationString = (duration: number) => {
    const totalMinutes = duration / 60
    const hours = Math.floor(totalMinutes / 60)
    const mins = Math.floor(totalMinutes % 60)

    return [
      t('subcategoryPage.tile.lessonInfoPill.separator'),
      hours
        ? t('subcategoryPage.tile.lessonInfoPill.hour', {
            numberOfHours: hours,
          })
        : '',
      mins
        ? t('subcategoryPage.tile.lessonInfoPill.min', {
            numberOfMinutes: mins,
          })
        : '',
    ].join(' ')
  }

  return (
    <Tile aspectRatio='auto' className={styles.tileContent}>
      <TileCaption position='right top' padding={2}>
        <Badge kind='dim'>
          <span className='mc-text--capitalize'>
            {t('subcategoryPage.tile.lessonInfoPill.type')}
            {content.durationInSecs &&
              getDurationString(content.durationInSecs)}
          </span>
        </Badge>
      </TileCaption>
      <TileOverlay
        className='mc-corners--3'
        color='25,28,33'
        type='gradient-bottom'
      />
      <TileCaption position='left bottom' padding={4}>
        <p className='mc-text-h5'>{content.title}</p>
      </TileCaption>
      <Link
        ariaHidden
        href={getTileLink(content, courseMeta)}
        tabIndex={-1}
        onClick={trackOnClick}
        passHref
      >
        <Image
          objectFit='cover'
          src={content.tileImgSrc}
          alt=''
          className='mc-corners--3'
          layout='fill'
          sizes={`(max-width: ${BREAKPOINTS.SM}px) 90vw, 25vw`}
          priority={isPrioritized}
        />
      </Link>
    </Tile>
  )
}
